// Map snapshots for walk thumbnails. One hidden map renders each requested view in turn and hands back
// an image, so a page full of walk cards uses a single WebGL context. The centre and zoom come from the
// server (WalkSketch), which draws the route over the image in matching coordinates. With labels: false
// (the tiny maps in walk pills) place and street names are left off, so only the streets show.

import { loadMapLibre, loadBaseStyle } from "./maplibre"

const IDLE_TIMEOUT_MS = 8000

let mapPromise = null
let queue = Promise.resolve()

export function renderStaticMap({ lng, lat, zoom, width, height, labels = true }) {
  const job = queue.then(async () => {
    const { map, container } = await sharedMap()

    // Turbo swaps the <body> on navigation, taking the hidden map with it
    if (!container.isConnected) document.body.appendChild(container)
    container.style.width = `${width}px`
    container.style.height = `${height}px`
    map.resize()
    map.jumpTo({ center: [lng, lat], zoom })
    showLabels(map, labels)

    await whenIdle(map)
    return map.getCanvas().toDataURL("image/webp", 0.85)
  })
  queue = job.catch(() => {})
  return job
}

function sharedMap() {
  mapPromise ||= (async () => {
    const [maplibregl, style] = await Promise.all([loadMapLibre(), loadBaseStyle()])

    const container = document.createElement("div")
    container.setAttribute("aria-hidden", "true")
    Object.assign(container.style, { position: "fixed", left: "-10000px", top: "0", pointerEvents: "none" })
    document.body.appendChild(container)

    const map = new maplibregl.Map({
      container,
      style,
      interactive: false,
      attributionControl: false,
      fadeDuration: 0,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      canvasContextAttributes: { preserveDrawingBuffer: true }
    })
    await new Promise((resolve, reject) => {
      map.once("load", resolve)
      map.once("error", reject)
    })
    return { map, container }
  })()

  return mapPromise.catch((error) => {
    mapPromise = null
    throw error
  })
}

// Only the label layers the style shows to begin with are switched, so hidden ones stay hidden
let labelLayers = null

function showLabels(map, visible) {
  labelLayers ||= (map.getStyle()?.layers || [])
    .filter(layer => layer.type === "symbol" && map.getLayoutProperty(layer.id, "visibility") !== "none")
    .map(layer => layer.id)

  const visibility = visible ? "visible" : "none"
  for (const id of labelLayers) {
    if (map.getLayoutProperty(id, "visibility") !== visibility) map.setLayoutProperty(id, "visibility", visibility)
  }
}

function whenIdle(map) {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, IDLE_TIMEOUT_MS)
    map.once("idle", () => {
      clearTimeout(timer)
      resolve()
    })
    map.triggerRepaint()
  })
}
